import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Copy, Trash2, X } from "lucide-react";
import {
  parseNewline, parseTabular, isTabular, addRow, removeRow, duplicateRow,
  applyValidation, summarize, submittableRows, ROW_STATUS,
} from "./batchCore";

// Reusable, module-agnostic batch editor. Modules pass their own `columns`,
// `validateRow`, `onCreate` (server call returning per-row results) and labels.
// No module fields/business rules live here.
export function BatchEditor({
  columns, validateRow, pasteColumn, aliasMap = {}, destinationLabel = "",
  createLabel = "Create", onCreate, onClose, annotate, notice = "", bulkFields = [],
}) {
  const [rows, setRows] = useState([]);
  const [hideDup, setHideDup] = useState(false);
  const isDup = (r) => r.status === ROW_STATUS.DUPLICATE || (r.warnings || []).some((w) => /duplicate/i.test(w));
  const set = (next) => {
    const validated = applyValidation(next, validateRow);
    setRows(annotate ? annotate(validated) : validated);
  };
  const bulkSet = (key, val) => set(rows.map((x) => ({ ...x, values: { ...x.values, [key]: val } })));
  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (isTabular(text)) { e.preventDefault(); set(parseTabular(text, { aliasMap }).rows); }
    else if (text.includes("\n") && pasteColumn) { e.preventDefault(); set(parseNewline(text, pasteColumn)); }
  };
  const summary = useMemo(() => summarize(rows), [rows]);
  const badge = (s) => ({
    [ROW_STATUS.VALID]: "bg-emerald-100 text-emerald-700", [ROW_STATUS.WARNING]: "bg-amber-100 text-amber-700",
    [ROW_STATUS.INVALID]: "bg-red-100 text-red-700", [ROW_STATUS.CREATED]: "bg-emerald-600 text-white",
    [ROW_STATUS.DUPLICATE]: "bg-amber-200 text-amber-800", [ROW_STATUS.FAILED]: "bg-red-200 text-red-800",
  }[s] || "bg-slate-100 text-slate-600");

  const create = async () => {
    const subset = submittableRows(rows);
    if (!subset.length || !onCreate) return;
    const next = await onCreate(subset, rows); // module reconciles + returns updated rows
    if (next) setRows(next); // raw set: keep server statuses (created/duplicate/failed), don't re-validate
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="batch-editor">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-800">Batch add {destinationLabel && <span className="text-slate-400 font-normal">· {destinationLabel}</span>}</div>
          <button onClick={onClose} data-testid="batch-close"><X size={18} /></button>
        </div>
        <div className="px-4 py-2 border-b bg-slate-50 text-xs" data-testid="batch-summary" onPaste={onPaste}>
          <span className="text-emerald-700 font-semibold">{summary.ready} ready</span>
          {summary.warning > 0 && <span className="ml-3 text-amber-700">{summary.warning} need attention</span>}
          {summary.invalid > 0 && <span className="ml-3 text-red-700">{summary.invalid} invalid</span>}
          {summary.duplicates > 0 && <span className="ml-3 text-amber-700">{summary.duplicates} possible duplicates</span>}
          {summary.duplicates > 0 && <button onClick={() => setHideDup((v) => !v)} className="ml-3 text-slate-500 underline" data-testid="batch-hide-duplicates">{hideDup ? "Show all" : "Hide duplicates"}</button>}
          <span className="ml-3 text-slate-400">Tip: paste from a spreadsheet or one item per line</span>
        </div>
        {notice && <div className="px-4 py-2 border-b bg-amber-50 text-[11px] text-amber-800" data-testid="batch-notice">{notice}</div>}
        {bulkFields.length > 0 && rows.length > 0 && (
          <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2" data-testid="batch-bulk-bar">
            {bulkFields.map((bf) => (
              <div key={bf.key} className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-500">{bf.label}</span>
                <select onChange={(e) => bulkSet(bf.key, e.target.value)} value=""
                  className="border border-slate-200 rounded px-2 py-1 bg-white"
                  data-testid={`batch-bulk-${bf.key}`}>
                  <option value="" disabled>Choose…</option>
                  {(bf.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-auto p-2" onPaste={onPaste}>
          <table className="w-full text-sm min-w-0">
            <thead><tr className="text-left text-xs text-slate-500">
              <th className="w-8 px-2">#</th>{columns.map((c) => <th key={c.key} className="px-2 py-1">{c.label}</th>)}<th className="w-24"></th><th className="w-8"></th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.rowId} data-testid={`batch-row-${i}`} className={`border-t ${hideDup && isDup(r) ? "hidden" : ""}`}>
                  <td className="px-2 text-slate-400">{i + 1}</td>
                  {columns.map((c) => {
                    const visible = c.visible ? c.visible(r.values) : true;
                    const disabled = r.status === ROW_STATUS.CREATED;
                    const change = (val) => set(rows.map((x) => x.rowId === r.rowId ? { ...x, values: { ...x.values, [c.key]: val } } : x));
                    return (
                      <td key={c.key} className="px-1 py-1">
                        {!visible ? (
                          <span className="text-slate-300 text-xs px-2" data-testid={`batch-cell-${i}-${c.key}-na`}>—</span>
                        ) : c.type === "select" ? (
                          <select value={r.values[c.key] ?? c.default ?? ""} disabled={disabled}
                            onChange={(e) => change(e.target.value)}
                            className={`w-full border rounded px-2 py-1 bg-white ${r.errors[c.key] ? "border-red-400" : "border-slate-200"}`}
                            data-testid={`batch-cell-${i}-${c.key}`}>
                            {(c.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input value={r.values[c.key] || ""} disabled={disabled} placeholder={c.placeholder || ""}
                            type={c.inputType || "text"}
                            onChange={(e) => change(e.target.value)}
                            className={`w-full border rounded px-2 py-1 ${r.errors[c.key] ? "border-red-400" : "border-slate-200"}`}
                            data-testid={`batch-cell-${i}-${c.key}`} />
                        )}
                      </td>
                    );
                  })}
                  <td className="px-1"><span className={`text-[10px] px-1.5 py-0.5 rounded ${badge(r.status)}`} data-testid={`batch-status-${i}`}>{r.status}</span></td>
                  <td className="px-1 flex gap-1">
                    <button onClick={() => set(duplicateRow(rows, r.rowId))} title="Duplicate"><Copy size={14} /></button>
                    <button onClick={() => set(removeRow(rows, r.rowId))} title="Remove" data-testid={`batch-remove-${i}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-10" data-testid="batch-empty">
              Paste from a spreadsheet or one item per line, or press "Add row".
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t gap-2">
          <div className="flex gap-2">
            <button onClick={() => set(addRow(rows))} className="text-sm flex items-center gap-1 px-2 py-1 rounded border" data-testid="batch-add-row"><Plus size={14} /> Add row</button>
            {rows.length > 0 && <button onClick={() => { if (!rows.some((x) => Object.values(x.values).some(Boolean)) || window.confirm("Clear all rows?")) set([]); }} className="text-sm text-slate-500" data-testid="batch-clear">Clear all</button>}
          </div>
          <button onClick={create} disabled={summary.submittableCount === 0} className="bg-brand hover:bg-brand-hover text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50" data-testid="batch-create">
            {createLabel} ({summary.submittableCount})
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
